import type { MiniappToParentNotificationInputOf } from "schemas/miniappRpc";

// The serializable shortcut descriptor sent over the bridge, derived from the
// registerShortcutItems notification payload so it stays in lockstep with the
// wire schema.
export type ShortcutItemDescriptor =
  MiniappToParentNotificationInputOf<"registerShortcutItems">["items"][number];

type ShortcutChildDescriptor = NonNullable<
  ShortcutItemDescriptor["children"]
>[number];

// A leaf under a drill-in parent (e.g. "Approved" under "Set status...").
export type ShortcutChildItem = ShortcutChildDescriptor & {
  onSelect: () => void;
};

// A miniapp-registered cmd-k item: the serializable descriptor plus the local
// `onSelect` that runs when the host reports the item was invoked. `onSelect`
// can't cross postMessage, so it stays in this module keyed by id; the host
// fires `shortcutInvoked({ itemId })` and the BedrockProvider routes it back to
// `invokeShortcut`. Parents with `children` are drill-ins (omit `onSelect`);
// leaves take `onSelect` and omit `children`.
export type ShortcutItem = Omit<ShortcutItemDescriptor, "children"> & {
  onSelect?: () => void;
  children?: ShortcutChildItem[];
};

// `onSelect` lives here only so the imperative invoke path can find it by id.
// Miniapps never read their registered items, so a plain module-level map is
// enough — no reactive store needed.
const onSelectByItemId = new Map<string, () => void>();

function collectOnSelectEntries(
  items: ShortcutItem[],
): { itemId: string; onSelect: () => void }[] {
  const entries: { itemId: string; onSelect: () => void }[] = [];
  for (const item of items) {
    if (item.onSelect) {
      entries.push({ itemId: item.id, onSelect: item.onSelect });
    }
    for (const child of item.children ?? []) {
      entries.push({ itemId: child.id, onSelect: child.onSelect });
    }
  }
  return entries;
}

export function collectShortcutItemIds(items: ShortcutItem[]): string[] {
  return items.flatMap((item) => [
    item.id,
    ...(item.children?.map((child) => child.id) ?? []),
  ]);
}

export function toSerializableShortcutItems(
  items: ShortcutItem[],
): ShortcutItemDescriptor[] {
  return items.map((item) => ({
    id: item.id,
    label: item.label,
    ...(item.group ? { group: item.group } : {}),
    ...(item.description ? { description: item.description } : {}),
    ...(item.icon ? { icon: item.icon } : {}),
    ...(item.children
      ? {
          children: item.children.map((child) => ({
            id: child.id,
            label: child.label,
            ...(child.description ? { description: child.description } : {}),
            ...(child.icon ? { icon: child.icon } : {}),
          })),
        }
      : {}),
  }));
}

export function registerShortcutOnSelects(items: ShortcutItem[]) {
  for (const { itemId, onSelect } of collectOnSelectEntries(items)) {
    onSelectByItemId.set(itemId, onSelect);
  }
}

export function unregisterShortcutOnSelects(itemIds: string[]) {
  for (const itemId of itemIds) {
    onSelectByItemId.delete(itemId);
  }
}

// Imperative invocation path used by BedrockProvider when the host reports a
// shortcut was selected.
export function invokeShortcut(itemId: string) {
  onSelectByItemId.get(itemId)?.();
}
